import i18next from "i18next";
import { PersistantStorage } from "../Utils/LocalStorage";
import { Request } from "./Request";
import { urls } from "../Utils/Urls";

const TransformArrayToObj = (translationList) => {
  return translationList.reduce(
    // eslint-disable-next-line
    (obj, item) => ((obj[item.code] = item.message), obj),
    {}
  );
};

const getUnique = (arr) => {
  return arr.filter((value, index, self) => self.indexOf(value) === index);
};

// One instance = one isolated fetch/cache pipeline (own cache keys, own i18next namespace).
export const createLocalizationService = ({ namespace, cachePrefix, cacheEmptyModules = true }) => {
  const LOCALE_LIST = (locale) => `${cachePrefix}.${locale}.List`;
  const LOCALE_ALL_LIST = () => `${cachePrefix}.List`;
  const LOCALE_MODULE = (locale, module) => `${cachePrefix}.${locale}.${module}`;

  const LocalizationStore = {
    getCaheData: (key) => PersistantStorage.get(key),
    setCacheData: (key, value) => {
      PersistantStorage.set(key, value, 86400);
    },
    getList: (locale) => LocalizationStore.getCaheData(LOCALE_LIST(locale)) || [],
    setList: (locale, namespaces) =>
      LocalizationStore.setCacheData(LOCALE_LIST(locale), namespaces),
    getAllList: () => LocalizationStore.getCaheData(LOCALE_ALL_LIST()) || [],
    setAllList: (namespaces) =>
      LocalizationStore.setCacheData(LOCALE_ALL_LIST(), namespaces),
    store: (locale, modules, messages) => {
      const AllNamespaces = LocalizationStore.getAllList();
      const Namespaces = LocalizationStore.getList(locale);
      modules.forEach((module) => {
        if (!Namespaces.includes(module)) {
          const moduleMessages = messages.filter(
            (message) => message.module === module
          );
          // A module the CMS does not have yet returns nothing; caching that as "loaded" hides it for
          // the 24h TTL once it is created. Instances that expect pending modules opt out.
          if (!cacheEmptyModules && moduleMessages.length === 0) return;
          Namespaces.push(module);
          LocalizationStore.setCacheData(
            LOCALE_MODULE(locale, module),
            moduleMessages
          );
        }
      });
      LocalizationStore.setCacheData(LOCALE_LIST(locale), Namespaces);
      LocalizationStore.setAllList(getUnique([...AllNamespaces, ...Namespaces]));
    },
    get: (locale, modules) => {
      const storedModules = LocalizationStore.getList(locale);
      const newModules = modules.filter(
        (module) => !storedModules.includes(module)
      );
      const messages = [];
      storedModules.forEach((module) => {
        const data = LocalizationStore.getCaheData(LOCALE_MODULE(locale, module)) || [];
        messages.push(...data);
      });
      return [newModules, messages];
    },
    updateResources: (messages, locale) => {
      const locales = TransformArrayToObj(messages);
      i18next.addResources(locale, namespace, locales);
    },
  };

  return {
    getLocale: async ({ modules = [], locale = "en_IN", tenantId }) => {
      if (locale.indexOf("_IN") === -1) {
        locale += "_IN";
      }

      const [newModules, messages] = LocalizationStore.get(locale, modules);
      if (newModules.length > 0) {
        const response = await Request({
          url: urls.localization,
          params: { module: newModules.join(","), locale, tenantId },
          useCache: false,
        });
        const fetched = response?.data?.messages;
        // Request() resolves with the error response instead of throwing; a
        // non-array here is a failed fetch and must NOT be cached as "loaded".
        if (!Array.isArray(fetched)) {
          throw new Error(`localisation fetch failed for ${newModules.join(",")} (${response?.status})`);
        }
        messages.push(...fetched);
        LocalizationStore.store(locale, newModules, fetched);
      }
      LocalizationStore.updateResources(messages, locale);
      return messages;
    },
    getList: (locale) => LocalizationStore.getList(locale),
    getAllList: () => LocalizationStore.getAllList(),
    updateResources: (messages, locale) => LocalizationStore.updateResources(messages, locale),
  };
};
